import {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useMemo,
    useSyncExternalStore,
    type ReactNode,
} from 'react';
import { useMatches } from 'react-router';

import { useRegion } from './region-context';

import {
    readTenantRegionFrom,
    readTenantRegionsSnapshot,
    rememberTenantRegion,
    subscribeTenantRegions,
} from '@/lib/tenant-region-hints';

interface TenantContextType {
    /** Persist a tenant as the selection to fall back on off a tenant route. */
    selectTenant: (tenantId: string) => void;
    /** URL tenant when the route names one, otherwise the stored selection. */
    tenantId: string | null;
    /** The remembered selection alone, ignoring the route. */
    storedTenantId: string | null;
    tenantRegion: string | null;
    clearTenant: () => void;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

const STORAGE_KEY = 'escloud-tenant-id';

/**
 * Get stored tenant ID from localStorage
 */
function getStoredTenantId(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(STORAGE_KEY);
}

function writeStoredTenantId(tenantId: string | null): void {
    // Storage can be full or blocked by policy (Safari private mode). Losing the
    // selection costs a redirect through TenantRedirect, so a failure here must
    // never propagate out of a render or a mutation.
    try {
        if (tenantId) {
            localStorage.setItem(STORAGE_KEY, tenantId);
        } else {
            localStorage.removeItem(STORAGE_KEY);
        }
    } catch {
        return;
    }
}

/**
 * Forget the remembered selection. Called on sign-out, so the next user of a
 * shared workstation does not inherit it.
 */
export function forgetStoredTenant(): void {
    writeStoredTenantId(null);
}

/**
 * Tenant id carried by the deepest matched route, from any of the routes that
 * scope themselves to a tenant (`/tenants/:tenantId`, `/settings/tenant/:tenantId`).
 */
function useRouteTenantId(): string | null {
    const matches = useMatches();

    for (let i = matches.length - 1; i >= 0; i--) {
        const { tenantId } = matches[i].params as { tenantId?: string };
        if (tenantId) {
            return tenantId;
        }
    }

    return null;
}

/**
 * Holds the tenant every tenant-scoped request is made against — the value
 * behind the `X-Tenant-Id` header and every tenant-keyed query.
 *
 * The URL is the authority whenever it names a tenant, and it is read
 * *during render*, not synced in an effect: the guards, the header and the
 * queries all have to agree on the first render after a switch or a deep link
 * (ESCLOUD-269). The stored selection is only the fallback for routes that
 * name no tenant.
 */
export function TenantProvider({ children }: { children: ReactNode }) {
    const { tenantClaims } = useRegion();

    const routeTenantId = useRouteTenantId();
    const [storedTenantId, setStoredTenantId] = useState<string | null>(getStoredTenantId);

    const tenantId = routeTenantId ?? storedTenantId;

    // Region is the one thing that must resolve synchronously, because it picks
    // the regional API every request goes to. The claim knows it for a claimed
    // tenant; for a claim-less one the hint recorded when this browser last saw
    // that tenant does. Everything else about the tenant — whether it exists at
    // all, its name, the user's role — is the server's to answer, and comes from
    // the tenant registry (useCurrentTenant); a claim can say none of it.
    //
    // The hint store is subscribed to, not merely read: the registry learns a
    // tenant's authoritative region only when its query returns, and a session
    // already rendering that tenant has to retarget its requests then and there
    // rather than at the next reload.
    const hintsSnapshot = useSyncExternalStore(subscribeTenantRegions, readTenantRegionsSnapshot, () => '');

    const tenantRegion = useMemo(() => {
        if (!tenantId) return null;
        const claim = tenantClaims.find((c) => c.id === tenantId);
        return claim?.region ?? readTenantRegionFrom(hintsSnapshot, tenantId);
    }, [hintsSnapshot, tenantClaims, tenantId]);

    // Keep the hint current, so the next reload can address this tenant even if
    // the claims never catch up.
    useEffect(() => {
        if (tenantId && tenantRegion) {
            rememberTenantRegion(tenantId, tenantRegion);
        }
    }, [tenantId, tenantRegion]);

    // NOTE: the route tenant is deliberately NOT persisted here. Only an
    // organization a regional API has vouched for becomes the remembered
    // selection (useConfirmedTenantPersistence) — otherwise a deep link to a
    // non-existent tenant would be remembered, and clearing a dead selection
    // would be undone by the route the user is being redirected away from.

    const selectTenant = useCallback((selectedTenantId: string) => {
        setStoredTenantId(selectedTenantId);
        writeStoredTenantId(selectedTenantId);
    }, []);

    const clearTenant = useCallback(() => {
        setStoredTenantId(null);
        writeStoredTenantId(null);
    }, []);

    return (
        <TenantContext.Provider
            value={{
                selectTenant,
                tenantId,
                storedTenantId,
                tenantRegion,
                clearTenant,
            }}
        >
            {children}
        </TenantContext.Provider>
    );
}

export function useTenant() {
    const context = useContext(TenantContext);
    if (!context) {
        throw new Error('useTenant must be used within a TenantProvider');
    }
    return context;
}
